/* eslint-disable no-param-reassign */
import {
  StateCreator,
  StoreApi,
  UseBoundStore,
  create,
} from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  acquireCanvasLock,
  releaseCanvasLock,
  releaseCanvasLockKeepalive,
  renewCanvasLock,
  type TeamCanvasLockSnapshot,
  type TeamCanvasLockUser,
} from '$/domains/workFlowV3/teamCanvasLockService';
import {
  addTeamCanvasLockBroadcastListener,
  broadcastTeamCanvasLock,
} from '$/utils/teamCanvasLockBroadcast';
import { getTeamCanvasLockTabId } from '$/utils/teamCanvasLockTabId';
import { isCanvasAccessDeniedError } from '$/api/utils/resolveCanvasResponseErrorCode';
import { useLanguageStore } from './languageStore';
import type { Write, WithImmer } from './immer.d';
import type { StoreSubscribeWithSelector } from './subscribe.d';

export type { TeamCanvasLockUser };

/** 画布锁页级角色 */
export type TeamCanvasLockRole =
  | 'idle'
  | 'checking'
  | 'holder'
  | 'viewer_locked'
  | 'viewer_available';

const RENEW_INTERVAL_MS = 5000;

interface StartCanvasLockSessionOptions {
  projectId?: string;
  onAccessDenied?: () => void;
  /** 当前账号 UID，用于团队锁未返回 tab_conflict 时识别同账号页签。 */
  currentUserId?: string | number;
  /** 个人版首次 renew 未持锁时直接抢占，不进入确认蒙层。 */
  autoAcquireOnStart?: boolean;
  /** 团队版确认是同一用户的其他页签时直接抢占。 */
  autoAcquireOnTabConflict?: boolean;
  /** 团队版锁空闲时自动续锁或接管，不进入“画布已释放”状态。 */
  autoAcquireReleasedLock?: boolean;
}

interface TeamCanvasLockState {
  role: TeamCanvasLockRole;
  roomId: string;
  projectId: string;
  tabId: string;
  locked: boolean;
  isHolder: boolean;
  canAcquire: boolean;
  tabConflict: boolean;
  holderUid: number;
  users: TeamCanvasLockUser[];
  errorMessage: string;
  /** 当前会话是否在锁空闲时自动 acquire。 */
  autoAcquireReleasedLock: boolean;
  /** 原持锁页检测到其他访问者后主动让锁，避免再次抢回。 */
  yieldedLockToVisitor: boolean;
  /** 最近一次 renew/acquire 快照时间 */
  updatedAt: number;
  /** 进房启动锁会话：renew → 可抢则 acquire → 开启 5s 心跳 */
  startLockSession: (
    roomId: string,
    options?: StartCanvasLockSessionOptions,
  ) => Promise<void>;
  /** 预览端确认进入：acquire 成功后切 holder */
  acquireAsViewer: () => Promise<boolean>;
  /** 持锁人主动释放 */
  releaseLock: () => Promise<void>;
  /** 切房 / 卸载时清理会话（持锁则尽力 release） */
  leaveLockSession: () => Promise<void>;
  resetLockState: () => void;
}

let renewTimer: ReturnType<typeof setInterval> | null = null;
let sessionGeneration = 0;
let broadcastUnsub: (() => void) | null = null;
let pageLifecycleBound = false;
/** 画布锁无权限时由宿主处理跳转（如 team_studio 回首页） */
let accessDeniedHandler: (() => void) | null = null;
/** 本页主动释放 / 跳转 / 刷新时置位，避免当前页误进入锁已释放状态。 */
let intentionalLeavePending = false;

/**
 * 标记本页即将离开或主动释放锁，抑制可进入蒙层。
 */
function markIntentionalLeave() {
  intentionalLeavePending = true;
}

/**
 * 清除主动离开标记（新会话启动时）。
 */
function clearIntentionalLeave() {
  intentionalLeavePending = false;
}

const initialStateFields: Omit<
TeamCanvasLockState,
'startLockSession' | 'acquireAsViewer' | 'releaseLock' | 'leaveLockSession' | 'resetLockState'
> = {
  role: 'idle',
  roomId: '',
  projectId: '',
  tabId: '',
  locked: false,
  isHolder: false,
  canAcquire: false,
  tabConflict: false,
  holderUid: 0,
  users: [],
  errorMessage: '',
  autoAcquireReleasedLock: false,
  yieldedLockToVisitor: false,
  updatedAt: 0,
};

/**
 * 判断快照中是否存在当前持锁人之外的预览访问者。
 */
function hasOtherViewingUsers(users: TeamCanvasLockUser[], holderUid: number): boolean {
  return users.some((user) => (
    user.status === 'viewing'
    && (holderUid <= 0 || user.uid !== holderUid)
  ));
}

/**
 * 从快照推导页级角色。
 * @param _preferred 预留：首进可抢由 startLockSession 直接 acquire；旁观等待态展示可进入蒙层
 */
function resolveRoleFromSnapshot(
  snapshot: TeamCanvasLockSnapshot,
  _preferred: 'enter' | 'waiting',
): TeamCanvasLockRole {
  if (snapshot.is_holder) {
    return 'holder';
  }
  if (snapshot.tab_conflict || snapshot.locked) {
    return 'viewer_locked';
  }
  if (snapshot.can_acquire) {
    return 'viewer_available';
  }
  return 'viewer_locked';
}

/**
 * 将快照字段合并进当前 draft。
 */
function applySnapshotToDraft(
  state: TeamCanvasLockState,
  snapshot: TeamCanvasLockSnapshot,
  role: TeamCanvasLockRole,
) {
  state.locked = snapshot.locked;
  state.isHolder = snapshot.is_holder;
  state.canAcquire = snapshot.can_acquire;
  state.tabConflict = snapshot.tab_conflict;
  state.holderUid = snapshot.holder_uid;
  state.users = snapshot.users || [];
  state.role = role;
  state.updatedAt = Date.now();
  state.errorMessage = '';
  if (snapshot.room_id) {
    state.roomId = String(snapshot.room_id);
  }
}

/**
 * 停止心跳定时器。
 */
function stopRenewTimer() {
  if (renewTimer) {
    clearInterval(renewTimer);
    renewTimer = null;
  }
}

/**
 * 画布锁接口无权限：清理会话并通知宿主跳转，避免展示占用蒙层。
 */
function handleLockAccessDeniedError(
  error: unknown,
  generation: number,
  resetState: () => void,
): boolean {
  if (!isCanvasAccessDeniedError(error) || generation !== sessionGeneration) {
    return false;
  }
  stopRenewTimer();
  sessionGeneration += 1;
  resetState();
  accessDeniedHandler?.();
  return true;
}

const teamCanvasLockState: StateCreator<TeamCanvasLockState, [
  ['zustand/immer', never],
  ['zustand/subscribeWithSelector', never],
]> = (set, get) => {
  /**
   * 执行一次 renew，并按角色更新状态。
   */
  const tickRenew = async (generation: number) => {
    const currentBeforeRenew = get();
    if (!currentBeforeRenew.projectId || !currentBeforeRenew.tabId || generation !== sessionGeneration) {
      return;
    }
    try {
      const snapshot = await renewCanvasLock({
        project_id: currentBeforeRenew.projectId,
        room_id: currentBeforeRenew.roomId || undefined,
        tab_id: currentBeforeRenew.tabId,
      });
      if (generation !== sessionGeneration) {
        return;
      }

      const roleBeforeTransition = currentBeforeRenew.role;
      const wasHolder = roleBeforeTransition === 'holder';

      // 本页主动释放/跳转后，renew 不应把当前页切成可进入蒙层
      if (intentionalLeavePending && (wasHolder || roleBeforeTransition === 'idle' || roleBeforeTransition === 'checking')) {
        intentionalLeavePending = false;
        return;
      }

      // 同账号新页签已接管时，不信任团队心跳按账号返回的 is_holder，直到本页主动抢回。
      if (currentBeforeRenew.yieldedLockToVisitor && currentBeforeRenew.tabConflict) {
        set((state) => {
          applySnapshotToDraft(state, snapshot, 'viewer_locked');
          state.locked = true;
          state.isHolder = false;
          state.canAcquire = false;
          state.tabConflict = true;
          state.yieldedLockToVisitor = true;
        });
        return;
      }

      const lockIsAvailable = (
        snapshot.can_acquire
        && !snapshot.locked
        && !snapshot.tab_conflict
      );
      if (currentBeforeRenew.autoAcquireReleasedLock && lockIsAvailable) {
        const hasOtherVisitors = wasHolder && (
          hasOtherViewingUsers(currentBeforeRenew.users, currentBeforeRenew.holderUid)
          || hasOtherViewingUsers(snapshot.users, currentBeforeRenew.holderUid)
        );
        if (currentBeforeRenew.yieldedLockToVisitor || hasOtherVisitors) {
          set((state) => {
            applySnapshotToDraft(state, snapshot, 'viewer_locked');
            state.yieldedLockToVisitor = true;
          });
          return;
        }

        try {
          const acquireSnapshot = await acquireCanvasLock({
            project_id: currentBeforeRenew.projectId,
            room_id: currentBeforeRenew.roomId || undefined,
            tab_id: currentBeforeRenew.tabId,
          });
          if (generation !== sessionGeneration) {
            return;
          }
          const nextRole = acquireSnapshot.is_holder ? 'holder' : 'viewer_locked';
          set((state) => {
            applySnapshotToDraft(state, acquireSnapshot, nextRole);
            if (nextRole === 'holder') {
              state.yieldedLockToVisitor = false;
            }
          });
          if (nextRole === 'holder') {
            broadcastTeamCanvasLock({
              type: 'holder_acquired',
              roomId: currentBeforeRenew.roomId,
              tabId: currentBeforeRenew.tabId,
            });
          }
          return;
        } catch (acquireError) {
          if (handleLockAccessDeniedError(acquireError, generation, () => {
            set((state) => {
              Object.assign(state, initialStateFields);
            });
          })) {
            return;
          }
          console.error('[teamCanvasLock] auto acquire released lock failed', acquireError);
          set((state) => {
            applySnapshotToDraft(state, snapshot, 'viewer_locked');
          });
          return;
        }
      }

      let nextRole: TeamCanvasLockRole;
      if (snapshot.is_holder) {
        nextRole = 'holder';
      } else if (wasHolder) {
        if (snapshot.can_acquire && !snapshot.locked && !snapshot.tab_conflict) {
          nextRole = 'viewer_available';
        } else {
          nextRole = 'viewer_locked';
        }
      } else if (roleBeforeTransition === 'viewer_available') {
        nextRole = snapshot.locked || snapshot.tab_conflict
          ? 'viewer_locked'
          : 'viewer_available';
      } else {
        nextRole = resolveRoleFromSnapshot(snapshot, 'waiting');
      }
      set((state) => {
        applySnapshotToDraft(state, snapshot, nextRole);
        if (nextRole === 'holder') {
          state.yieldedLockToVisitor = false;
        }
      });
    } catch (error) {
      if (handleLockAccessDeniedError(error, generation, () => {
        set((state) => {
          Object.assign(state, initialStateFields);
        });
      })) {
        return;
      }
      console.error('[teamCanvasLock] renew failed', error);
    }
  };

  /**
   * 启动 5s renew 心跳。
   */
  const startRenewTimer = (generation: number) => {
    stopRenewTimer();
    renewTimer = setInterval(() => {
      tickRenew(generation).catch(() => undefined);
    }, RENEW_INTERVAL_MS);
  };

  /**
   * 绑定页签关闭 / 隐藏时的尽力释放。
   */
  const bindPageLifecycle = () => {
    if (typeof window === 'undefined' || pageLifecycleBound) {
      return;
    }
    pageLifecycleBound = true;

    const handlePageHide = () => {
      const current = get();
      if (current.role !== 'holder' || !current.projectId || !current.tabId) {
        return;
      }
      markIntentionalLeave();
      // 卸载场景用 keepalive，避免普通异步 POST 被浏览器取消
      releaseCanvasLockKeepalive({
        project_id: current.projectId,
        room_id: current.roomId || undefined,
        tab_id: current.tabId,
      });
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);
  };

  return {
    ...initialStateFields,

    /**
     * 启动画布锁会话；团队版还可在释放后自动续锁或接管空闲锁。
     */
    startLockSession: async (roomId: string, options?: StartCanvasLockSessionOptions) => {
      stopRenewTimer();
      sessionGeneration += 1;
      const generation = sessionGeneration;
      clearIntentionalLeave();
      accessDeniedHandler = options?.onAccessDenied ?? null;
      const tabId = getTeamCanvasLockTabId();

      set((state) => {
        Object.assign(state, {
          ...initialStateFields,
          role: 'checking' as const,
          roomId,
          tabId,
          autoAcquireReleasedLock: options?.autoAcquireReleasedLock === true,
        });
      });

      bindPageLifecycle();

      if (!broadcastUnsub) {
        broadcastUnsub = addTeamCanvasLockBroadcastListener((message) => {
          const current = get();
          if (message.roomId !== current.roomId || message.tabId === current.tabId) {
            return;
          }
          if (message.type === 'holder_acquired') {
            set((state) => {
              state.role = 'viewer_locked';
              state.locked = true;
              state.isHolder = false;
              state.canAcquire = false;
              state.tabConflict = true;
              state.yieldedLockToVisitor = true;
              state.updatedAt = Date.now();
            });
          }
          if (message.type === 'holder_released' && current.role === 'viewer_locked') {
            if (intentionalLeavePending) {
              return;
            }
            if (current.autoAcquireReleasedLock) {
              set((state) => {
                state.locked = false;
                state.canAcquire = true;
              });
              tickRenew(sessionGeneration).catch(() => undefined);
              return;
            }
            set((state) => {
              state.role = 'viewer_available';
              state.locked = false;
              state.canAcquire = true;
            });
          }
        });
      }

      try {
        const projectIdFromOptions = options?.projectId?.trim();
        // project_id 由进房 URL 透传；无则用 room_id
        const resolvedProjectId = projectIdFromOptions || roomId;

        set((state) => {
          state.projectId = resolvedProjectId;
        });

        // 进房锁流程第一步：renew 查询当前是否已上锁 / 在场状态
        const renewSnapshot = await renewCanvasLock({
          project_id: resolvedProjectId,
          room_id: roomId,
          tab_id: tabId,
        });
        if (generation !== sessionGeneration) {
          return;
        }

        if (renewSnapshot.is_holder) {
          set((state) => {
            applySnapshotToDraft(state, renewSnapshot, 'holder');
            state.yieldedLockToVisitor = false;
          });
          // 团队锁可能在同账号新页签首次 renew 时已返回持锁，仍需通知旧页签进入蒙层。
          broadcastTeamCanvasLock({
            type: 'holder_acquired',
            roomId,
            tabId,
          });
          startRenewTimer(generation);
          return;
        }

        const shouldAcquireAfterRenew = (
          renewSnapshot.can_acquire
          && !renewSnapshot.locked
          && !renewSnapshot.tab_conflict
        ) || options?.autoAcquireOnStart === true || (
          options?.autoAcquireOnTabConflict === true
          && (
            renewSnapshot.tab_conflict
            || (
              Boolean(options.currentUserId)
              && String(renewSnapshot.holder_uid) === String(options.currentUserId)
            )
          )
        );
        if (shouldAcquireAfterRenew) {
          try {
            const acquireSnapshot = await acquireCanvasLock({
              project_id: resolvedProjectId,
              room_id: roomId,
              tab_id: tabId,
            });
            if (generation !== sessionGeneration) {
              return;
            }
            if (acquireSnapshot.is_holder) {
              set((state) => {
                applySnapshotToDraft(state, acquireSnapshot, 'holder');
                state.yieldedLockToVisitor = false;
              });
              broadcastTeamCanvasLock({
                type: 'holder_acquired',
                roomId,
                tabId,
              });
              startRenewTimer(generation);
              return;
            }
            set((state) => {
              const waitingRole = options?.autoAcquireReleasedLock === true
                ? 'viewer_locked'
                : resolveRoleFromSnapshot(acquireSnapshot, 'waiting');
              applySnapshotToDraft(
                state,
                acquireSnapshot,
                waitingRole,
              );
            });
            startRenewTimer(generation);
            return;
          } catch (acquireError) {
            if (handleLockAccessDeniedError(acquireError, generation, () => {
              set((state) => {
                Object.assign(state, initialStateFields);
              });
            })) {
              return;
            }
            console.error('[teamCanvasLock] acquire failed', acquireError);
            set((state) => {
              applySnapshotToDraft(state, renewSnapshot, 'viewer_locked');
            });
            startRenewTimer(generation);
            return;
          }
        }

        set((state) => {
          applySnapshotToDraft(
            state,
            renewSnapshot,
            resolveRoleFromSnapshot(renewSnapshot, 'waiting'),
          );
        });
        startRenewTimer(generation);
      } catch (error) {
        console.error('[teamCanvasLock] startLockSession failed', error);
        if (generation !== sessionGeneration) {
          return;
        }
        if (handleLockAccessDeniedError(error, generation, () => {
          set((state) => {
            Object.assign(state, initialStateFields);
          });
        })) {
          return;
        }
        const current = get();
        const { languageStore } = useLanguageStore.getState();
        set((state) => {
          state.role = 'viewer_locked';
          state.errorMessage = languageStore.smartcatTranslationText(
            (v) => v.canvas_lock_status_load_failed_refresh_retry,
          );
        });
        // 已拿到 projectId 时仍启心跳，便于后续自动恢复
        if (current.projectId && current.tabId) {
          startRenewTimer(generation);
        }
      }
    },

    acquireAsViewer: async () => {
      const current = get();
      if (!current.projectId || !current.tabId) {
        return false;
      }
      try {
        const snapshot = await acquireCanvasLock({
          project_id: current.projectId,
          room_id: current.roomId || undefined,
          tab_id: current.tabId,
        });
        if (snapshot.is_holder) {
          set((state) => {
            applySnapshotToDraft(state, snapshot, 'holder');
            state.yieldedLockToVisitor = false;
          });
          broadcastTeamCanvasLock({
            type: 'holder_acquired',
            roomId: current.roomId,
            tabId: current.tabId,
          });
          return true;
        }
        set((state) => {
          const waitingRole = current.autoAcquireReleasedLock
            ? 'viewer_locked'
            : resolveRoleFromSnapshot(snapshot, 'waiting');
          applySnapshotToDraft(state, snapshot, waitingRole);
        });
        return false;
      } catch (error) {
        if (handleLockAccessDeniedError(error, sessionGeneration, () => {
          set((state) => {
            Object.assign(state, initialStateFields);
          });
        })) {
          return false;
        }
        console.error('[teamCanvasLock] acquireAsViewer failed', error);
        const { languageStore } = useLanguageStore.getState();
        set((state) => {
          state.role = 'viewer_locked';
          state.errorMessage = languageStore.smartcatTranslationText(
            (v) => v.get_edit_permission_failed_retry_later,
          );
        });
        return false;
      }
    },

    releaseLock: async () => {
      const current = get();
      if (!current.projectId || !current.tabId) {
        return;
      }
      markIntentionalLeave();
      try {
        const snapshot = await releaseCanvasLock({
          project_id: current.projectId,
          room_id: current.roomId || undefined,
          tab_id: current.tabId,
        });
        const nextRole = snapshot.locked || snapshot.tab_conflict
          ? 'viewer_locked'
          : 'idle';
        set((state) => {
          applySnapshotToDraft(state, snapshot, nextRole);
        });
        broadcastTeamCanvasLock({
          type: 'holder_released',
          roomId: current.roomId,
          tabId: current.tabId,
        });
      } catch (error) {
        console.error('[teamCanvasLock] releaseLock failed', error);
        set((state) => {
          state.role = 'idle';
          state.isHolder = false;
          state.locked = false;
          state.canAcquire = true;
        });
        broadcastTeamCanvasLock({
          type: 'holder_released',
          roomId: current.roomId,
          tabId: current.tabId,
        });
      }
    },

    leaveLockSession: async () => {
      const current = get();
      const shouldRelease = current.role === 'holder' && !!current.projectId && !!current.tabId;
      if (shouldRelease) {
        markIntentionalLeave();
      }
      const releaseParams = shouldRelease
        ? {
          project_id: current.projectId,
          room_id: current.roomId || undefined,
          tab_id: current.tabId,
        }
        : null;
      const releaseRoomId = current.roomId;
      const releaseTabId = current.tabId;

      stopRenewTimer();
      // 先作废旧会话并同步清空，避免 await release 期间新会话已被写成 checking/holder 后又被 idle 覆盖
      sessionGeneration += 1;
      accessDeniedHandler = null;
      set((state) => {
        Object.assign(state, initialStateFields);
      });

      if (!releaseParams) {
        return;
      }
      try {
        await releaseCanvasLock(releaseParams);
        broadcastTeamCanvasLock({
          type: 'holder_released',
          roomId: releaseRoomId,
          tabId: releaseTabId,
        });
      } catch (error) {
        console.error('[teamCanvasLock] leaveLockSession release failed', error);
      }
    },

    resetLockState: () => {
      stopRenewTimer();
      sessionGeneration += 1;
      set((state) => {
        Object.assign(state, initialStateFields);
      });
    },
  };
};

const useTeamCanvasLockStore: UseBoundStore<
Write<WithImmer<StoreApi<TeamCanvasLockState>>, StoreSubscribeWithSelector<TeamCanvasLockState>>
> = create<TeamCanvasLockState>()(immer(subscribeWithSelector(teamCanvasLockState)));

export default useTeamCanvasLockStore;

/**
 * 从 users 中取持锁编辑者（用于占用蒙层展示）。
 */
export function pickLockHolderUser(users: TeamCanvasLockUser[]): TeamCanvasLockUser | undefined {
  return users.find((user) => user.status === 'editing') || users[0];
}

/**
 * 从 users 中取预览者列表（持锁人顶栏通知）。
 */
export function pickLockViewingUsers(users: TeamCanvasLockUser[]): TeamCanvasLockUser[] {
  return users.filter((user) => user.status === 'viewing');
}
